import VueCookie from 'vue-cookie';
import axios, { AxiosInstance } from 'axios';
import { ENDPOINTS } from '@/settings/endpoints';

export class LinkedInTokenExpiredOrNotLogged extends Error {
  constructor() {
    super('LinkedIn token expired or not logged');
  }
}

export interface CollaborationResponse<T> {
  data: T;
  message: string;
  identifier: string;
}

export interface LinkedInAccessToken {
  accessToken: string;
  expiresIn: number;
}

export interface LinkedInTokenIntrospection {
  status: string;
  active: boolean;
}

export interface LinkedInUserData {
  id: string;
  userFullName: string;
  profilePictureUrl: string | null;
}

export interface LinkedInSharedPost {
  publicationUrl: string;
}

export class MicrocertificateLinkedInService {
  private readonly client: AxiosInstance;

  private constructor(accessToken: string) {
    const tokenD = VueCookie.get('d', { domain: '.descomplica.com.br' });
    this.client = axios.create({
      baseURL: ENDPOINTS.pegasusPepeLegal,
      headers: {
        Authorization: `Bearer ${tokenD}`,
        'x-linkedin-access-token': `Bearer ${accessToken}`,
      },
    });
  }

  static create(accessToken?: string | null): MicrocertificateLinkedInService {
    if (!accessToken) throw new LinkedInTokenExpiredOrNotLogged();

    return new MicrocertificateLinkedInService(accessToken);
  }

  static async validate(accessToken?: string | null): Promise<void> {
    if (!accessToken) throw new LinkedInTokenExpiredOrNotLogged();

    const tokenIntrospection = await this.introspectToken(accessToken);
    if (tokenIntrospection.active !== true) throw new LinkedInTokenExpiredOrNotLogged();
  }

  static getAuthUrl(): { authUrl: string; state: string } {
    const authUrl = new URL(ENDPOINTS.linkedInAuthEndpoint);

    const state = this.createRandomAuthState();
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('client_id', process.env.VUE_APP_LINKEDIN_CLIENT_ID || '');
    authUrl.searchParams.append('redirect_uri', ENDPOINTS.linkedInCallbackUri);
    authUrl.searchParams.append('scope', 'r_liteprofile w_member_social');
    authUrl.searchParams.append('state', state);

    return {
      authUrl: authUrl.toString(),
      state,
    };
  }

  private static async introspectToken(accessToken: string): Promise<LinkedInTokenIntrospection> {
    const tokenD = VueCookie.get('d', { domain: '.descomplica.com.br' });

    const { data: response } = await axios.post<CollaborationResponse<LinkedInTokenIntrospection>>(
      '/microcertificates/get-linkedin-token-introspection',
      null,
      {
        baseURL: ENDPOINTS.pegasusPepeLegal,
        headers: {
          'x-linkedin-access-token': `Bearer ${accessToken}`,
          Authorization: `Bearer ${tokenD}`,
        },
      },
    );

    return response.data;
  }

  static async authorizeUser(code: string): Promise<LinkedInAccessToken> {
    const tokenD = VueCookie.get('d', { domain: '.descomplica.com.br' });

    const { data: response } = await axios.post<CollaborationResponse<LinkedInAccessToken>>(
      '/microcertificates/authorize-linkedin-user',
      null,
      {
        baseURL: ENDPOINTS.pegasusPepeLegal,
        headers: {
          'x-linkedin-code': code,
          Authorization: `Bearer ${tokenD}`,
        },
      },
    );

    return response.data;
  }

  async getUserData(): Promise<LinkedInUserData> {
    const { data: response } = await this.client.get<CollaborationResponse<LinkedInUserData>>(
      '/microcertificates/get-linkedin-user-data',
    );

    return response.data;
  }

  async shareMicrocertificate(
    postText: string,
    microcertificateImageUrl: string,
  ): Promise<LinkedInSharedPost> {
    const { data: response } = await this.client.post<CollaborationResponse<LinkedInSharedPost>>(
      '/microcertificates/share-microcertificate-on-linkedin',
      {
        postText,
        microcertificateImageUrl,
      },
    );

    return response.data;
  }

  private static createRandomAuthState(size = 20) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;

    const state = Array(size)
      .fill(null)
      .map(() => characters.charAt(Math.floor(Math.random() * charactersLength)))
      .join('');

    return state;
  }
}
